import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { FiSearch, FiPlus, FiTrash2, FiAlertTriangle, FiChevronLeft, FiChevronRight, FiInbox } from 'react-icons/fi'
import '../../styles/AdminTable.css'

const ITEMS_PER_PAGE = 10
const API = process.env.REACT_APP_API_URL

const TableAdmin = () => {
  const authorization = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${authorization}` }
  const navigate = useNavigate()

  const [infoad, setInfoad] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const handleAuthError = (err) => {
    if ([401, 403, 404].includes(err.response?.status)) {
      localStorage.clear()
      navigate('/login')
    }
  }

  const fetchData = () => {
    setLoading(true)
    axios.get(`${API}/admin/lister`, { headers })
      .then((res) => setInfoad(res.data))
      .catch((err) => handleAuthError(err))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [])

  const filtered = useMemo(() => {
    let arr = [...infoad]
    if (search.trim()) {
      const s = search.toLowerCase()
      arr = arr.filter((a) =>
        a.nom?.toLowerCase().includes(s) ||
        a.prenom?.toLowerCase().includes(s) ||
        a.email?.toLowerCase().includes(s)
      )
    }
    return arr
  }, [infoad, search])

  const nbPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE))
  const pageData = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  useEffect(() => { setPage(1) }, [search])

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const toggleSelectAll = () => setSelected(selected.length === pageData.length ? [] : pageData.map((a) => a._id))

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await axios.get(`${API}/admin/${deleteTarget._id}/delete`, { headers })
      setInfoad((a) => a.filter((x) => x._id !== deleteTarget._id))
      setSelected((s) => s.filter((x) => x !== deleteTarget._id))
      toast.success('Administrateur supprimé')
      setDeleteTarget(null)
    } catch (err) {
      toast.error('Échec de la suppression')
      handleAuthError(err)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!selected.length) return
    if (!window.confirm(`Supprimer ${selected.length} administrateur(s) sélectionné(s) ?`)) return
    try {
      await Promise.all(selected.map((id) => axios.get(`${API}/admin/${id}/delete`, { headers })))
      setInfoad((a) => a.filter((x) => !selected.includes(x._id)))
      setSelected([])
      toast.success('Administrateurs supprimés')
    } catch (err) {
      toast.error('Échec de la suppression groupée')
      handleAuthError(err)
    }
  }

  return (
    <div className="atl-page">
      <Toaster />

      <div className="atl-header">
        <div>
          <h1 className="atl-title">Administrateurs</h1>
          <p className="atl-subtitle">{loading ? 'Chargement…' : `${filtered.length} administrateur${filtered.length > 1 ? 's' : ''}`}</p>
        </div>
        <Link to="/admin/Registre/ajouter" className="atl-btn-add">
          <FiPlus size={16} />
          Ajouter un administrateur
        </Link>
      </div>

      <div className="atl-toolbar">
        <div className="atl-search">
          <FiSearch size={15} />
          <input
            type="text"
            placeholder="Rechercher un nom, prénom ou email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="atl-search__clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        {selected.length > 0 && (
          <button className="atl-bulk-delete" onClick={handleBulkDelete}>
            <FiTrash2 size={14} />
            Supprimer ({selected.length})
          </button>
        )}
      </div>

      <div className="atl-table-wrap">
        <table className="atl-table">
          <thead>
            <tr>
              <th className="atl-th-check">
                <input type="checkbox" checked={pageData.length > 0 && selected.length === pageData.length} onChange={toggleSelectAll} />
              </th>
              <th>#</th>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Email</th>
              <th>Date de naissance</th>
              <th className="atl-th-actions">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="atl-skeleton-row"><td colSpan={7}><div className="atl-skeleton" /></td></tr>
              ))
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={7} className="atl-empty">
                  <FiInbox size={36} />
                  <p>Aucun administrateur trouvé</p>
                </td>
              </tr>
            ) : (
              pageData.map((a, i) => (
                <tr key={a._id} className={selected.includes(a._id) ? 'atl-row--selected' : ''}>
                  <td className="atl-th-check">
                    <input type="checkbox" checked={selected.includes(a._id)} onChange={() => toggleSelect(a._id)} />
                  </td>
                  <td>{(page - 1) * ITEMS_PER_PAGE + i + 1}</td>
                  <td><span className="atl-title-cell">{a.nom}</span></td>
                  <td>{a.prenom}</td>
                  <td>{a.email}</td>
                  <td><span className="atl-dims">{a.date}</span></td>
                  <td className="atl-th-actions">
                    <div className="atl-actions">
                      <button className="atl-action-btn atl-action-btn--delete" title="Supprimer" onClick={() => setDeleteTarget(a)}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && nbPages > 1 && (
        <div className="atl-pagination">
          <button className="atl-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}><FiChevronLeft /></button>
          {Array.from({ length: nbPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`atl-page-btn ${p === page ? 'atl-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="atl-page-btn" disabled={page === nbPages} onClick={() => setPage((p) => p + 1)}><FiChevronRight /></button>
        </div>
      )}

      {deleteTarget && (
        <div className="atl-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="atl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="atl-modal__icon"><FiAlertTriangle size={22} /></div>
            <h3 className="atl-modal__title">Supprimer cet administrateur ?</h3>
            <p className="atl-modal__text"><strong>{deleteTarget.nom} {deleteTarget.prenom}</strong> sera supprimé définitivement. Cette action est irréversible.</p>
            <div className="atl-modal__actions">
              <button className="atl-modal__btn atl-modal__btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
              <button className="atl-modal__btn atl-modal__btn--confirm" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableAdmin
